import { useOidc } from '@axa-fr/react-oidc';
import { TextLink } from '@edsn/pie-components';
import IconChevron from '@iconify/icons-ic/baseline-keyboard-arrow-down';
import IconCheck from '@iconify/icons-material-symbols/check';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuPortal,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@radix-ui/react-dropdown-menu';
import { useTranslation } from 'react-i18next';
import { useCurrentOrganisation } from '#pie/auth';
import { Icon } from '#pie/components/icon/Icon';
import { Text } from '#pie/components/text/Text';
import { VITE_PUBLIC_URL } from '#pie/env';
import {
  useGetContactDetailsId,
  useGetUserOrganisationInformation,
} from '#pie/internal/api/pie-bff';

export const HeaderProfile = () => {
  const { logout } = useOidc();
  const { t } = useTranslation();

  const { data: allAccounts } = useGetUserOrganisationInformation();
  const { currentAccount, storeAccountAndLogin } = useCurrentOrganisation();
  const userId = currentAccount?.userId;
  const { data: user, isPending: userLoading } = useGetContactDetailsId(
    userId!,
  );

  if (!allAccounts || !currentAccount) {
    return null;
  }
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className="flex shrink-0 cursor-pointer flex-col items-start justify-between text-left focus:outline-hidden"
          aria-label={t('header_profile.open_profile.aria-label')}
          type="button"
        >
          <div className="space-between flex w-full items-center justify-between gap-4 max-h-14.5">
            <div className="flex flex-col items-start justify-center">
              <Text className="text-content-heading hidden text-base font-bold sm:block">
                {currentAccount.name}
              </Text>

              {userLoading ? (
                <div
                  data-testid="skeleton"
                  className="h-5.25 w-32 animate-pulse bg-surface-skeleton sm:block"
                ></div>
              ) : (
                <Text className="hidden text-sm text-content-body sm:block">
                  {user?.email}
                </Text>
              )}
            </div>
            <Icon
              icon={IconChevron}
              size={20}
              className="text-content-heading align-top"
            />
          </div>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuPortal>
        <DropdownMenuContent
          sideOffset={24}
          align="end"
          className="z-20 min-w-72 max-w-[320px] overflow-auto rounded-md border border-border-menu bg-surface-base shadow-menu"
        >
          <DropdownMenuLabel>
            <div className="flex flex-col px-4 py-3">
              <Text className="text-content-heading font-bold">
                {user?.fullName}
              </Text>
              <div className="max-w-[320px]:flex-col flex flex-row flex-wrap justify-between gap-4">
                <Text className="text-sm text-content-body">{user?.email}</Text>
                <TextLink
                  aria-label={t('common.profile')}
                  href="/account"
                  className="text-sm underline"
                >
                  {t('common.profile')}
                </TextLink>
              </div>
            </div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator className="h-px bg-border-divider" />
          <DropdownMenuGroup>
            {allAccounts.items.map((company, idx) => {
              const isActive = company.ean13 === currentAccount.ean13;
              return isActive ? (
                <DropdownMenuItem asChild key={idx}>
                  <div className="text-content-heading flex w-full flex-row items-center justify-between px-4 py-3 text-left font-bold data-highlighted:outline-hidden">
                    <Text className="text-base font-bold">{company.name}</Text>
                    <Icon
                      icon={IconCheck}
                      size={20}
                      className="text-content-heading"
                    />
                  </div>
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem asChild key={idx}>
                  <button
                    onClick={() =>
                      storeAccountAndLogin({
                        ...company,
                        componentRoles: currentAccount.componentRoles,
                      })
                    }
                    aria-label={`${t(
                      'header_profile.select_organisation.aria-label',
                    )} ${company.name}`}
                    className="text:sm flex w-full cursor-pointer flex-col px-4 py-3 text-left data-highlighted:bg-surface-minimal-teal data-highlighted:outline-hidden"
                    type="button"
                  >
                    <Text className="text-base">{company.name}</Text>
                  </button>
                </DropdownMenuItem>
              );
            })}
            <DropdownMenuSeparator className="h-px bg-border-divider" />
            <DropdownMenuItem asChild>
              <button
                type="button"
                onClick={() => logout(VITE_PUBLIC_URL)}
                aria-label={t('common.log_out')}
                className="underline-none flex w-full px-4 py-3 text-content-body data-highlighted:bg-surface-minimal-teal data-highlighted:outline-hidden"
              >
                {t('common.log_out')}
              </button>
            </DropdownMenuItem>
          </DropdownMenuGroup>
        </DropdownMenuContent>
      </DropdownMenuPortal>
    </DropdownMenu>
  );
};
